import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import {
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Badge,
  Box,
  InputAdornment,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { Send as SendIcon, Search as SearchIcon } from "@mui/icons-material";
import api from "../services/api";

// --- FUNCTION TO STANDARDIZE COMMENT OBJECTS ---
const normalizeCommentData = (rawComment) => {
  // If input is invalid, log error and return a default error comment object
  if (!rawComment || typeof rawComment !== 'object') {
    console.error("normalizeCommentData: Invalid input", rawComment);
    return {
      id: `error-${Date.now()}`,
      comment: 'Error: Malformed comment data.',
      user: 'System',
      timestamp: new Date().toISOString(),
      is_read: true,
      is_pinned: false,
    };
  }

  // Destructure fields with defaults
  let { comment = '', timestamp, ...rest } = rawComment;

  // Ensure 'comment' is a string
  if (comment && typeof comment === 'object' && !Array.isArray(comment)) {
    // If comment is an object, try to extract common properties or stringify
    comment = comment.text ?? comment.body ?? JSON.stringify(comment);
  } else if (comment == null) {
    comment = '';
  } else if (typeof comment !== 'string') {
    comment = String(comment);
  }

  // Normalize timestamp into ISO string
  if (timestamp) {
    if (typeof timestamp === 'object') {
      // Handle Firestore Timestamp
      if (typeof timestamp.toDate === 'function') {
        timestamp = timestamp.toDate().toISOString();
      } else if (typeof timestamp.seconds === 'number') {
        // Handle custom object with seconds/nanoseconds
        const date = new Date(timestamp.seconds * 1000);
        if (typeof timestamp.nanoseconds === 'number') {
          date.setMilliseconds(
            date.getMilliseconds() + Math.floor(timestamp.nanoseconds / 1e6)
          );
        }
        timestamp = date.toISOString();
      } else if (timestamp instanceof Date) {
        timestamp = timestamp.toISOString();
      } else {
        // Fallback: try converting to string
        const d = new Date(String(timestamp));
        timestamp = isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
      }
    } else if (typeof timestamp === 'number') {
      // Raw epoch milliseconds
      timestamp = new Date(timestamp).toISOString();
    } else if (typeof timestamp === 'string') {
      // Parse date string
      const d = new Date(timestamp);
      timestamp = isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
    } else {
      // Unexpected type: fallback to now
      timestamp = new Date().toISOString();
    }
  } else {
    // Missing timestamp: use now
    timestamp = new Date().toISOString();
  }

  // Merge back and return
  return { ...rest, comment, timestamp };
};

// --- HEADER BAR FOR CHAT ---
const ChatHeader = ({ userName, userImage, title }) => (
  <Box
    sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: { xs: "16px", sm: "24px" }, borderBottom: "2px solid #e5e7eb",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
      <Box sx={{ position: "relative" }}>
        <Avatar src={userImage} sx={{ width: 40, height: 40 }}>
          {/* Show first letter if no image */}
          {!userImage && userName ? userName.charAt(0).toUpperCase() : null}
        </Avatar>
        {/* Online status indicator in bottom-right corner */}
        <Box
          sx={{ position: "absolute", right: 0, bottom: 0, width: 16, height: 16,
            borderRadius: "50%", backgroundColor: "green", border: "2px solid white",
          }}
        />
      </Box>
      <Box>
        <Typography variant="h6" sx={{ color: "#374151" }}>
          {userName || "User"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#4b5563" }}>
          {title || "Discussion"}
        </Typography>
      </Box>
    </Box>
    <Box>
      {/* Action icons */}
      <IconButton aria-label="search"><SearchIcon /></IconButton>
      <IconButton aria-label="favorite"><StarBorderIcon /></IconButton>
      <IconButton aria-label="visibility"><VisibilityIcon /></IconButton>
    </Box>
  </Box>
);

// --- INPUT AREA WITH @MENTION SUPPORT AND SUBMIT BUTTON ---
const ChatInput = ({ comment, setComment, handleCommentSubmit,
  loading, fetchUsersbySegment, users, usersLoading, setSelectedMentions }) => {

  const [anchorEl, setAnchorEl] = useState(null);

  // Show mention menu on right-click
  const handleContextMenu = (event) => {
    event.preventDefault();
    fetchUsersbySegment(); // Load users for mentions
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);
  const handleMenuItemClick = (name) => {
    setComment(prev => prev + ` @${name} `);
    setSelectedMentions(prev => [...prev, name]);
    handleMenuClose();
  };

  return (
    <Box
      component="form"
      onSubmit={handleCommentSubmit}
      sx={{ display: "flex", alignItems: "center", padding: "16px",
        borderTop: "2px solid #e5e7eb" }}
    >
      <TextField
        fullWidth multiline rows={2}
        placeholder="Write your message!"
        value={comment}
        onContextMenu={handleContextMenu}
        onChange={e => setComment(e.target.value)}
        variant="outlined"
        sx={{ backgroundColor: "#f3f4f6", borderRadius: "8px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="submit" disabled={loading}
                aria-label="send message"
                sx={{ backgroundColor: "#3b82f6", color: "white",
                  borderRadius: "50%", padding: "10px",
                  "&:hover": { backgroundColor: "#2563eb" },
                  "&.Mui-disabled": { backgroundColor: "#9ca3af" },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit"/> : <SendIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {/* Mention dropdown menu */}
      <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleMenuClose}>
        {usersLoading ? (
          <MenuItem disabled>
            <CircularProgress size={20} sx={{ mr: 1 }} /> Loading users...
          </MenuItem>
        ) : users.length > 0 ? (
          users.map((u, i) => (
            <MenuItem key={i} onClick={() => handleMenuItemClick(u.username || u)}>
              {u.username || u}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No users found</MenuItem>
        )}
      </Menu>
    </Box>
  );
};

// --- MAIN PAGE COMPONENT: LOADS PROFILE, COMMENTS, HANDLES EVENTS ---
export const JTDiscussionPage = () => {
  const location = useLocation();
  const { segmentId } = location.state || {};
  const email = localStorage.getItem("username");

  // Local state for UI
  const [commentInput, setCommentInput] = useState("");
  const [comments, setComments] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUserName, setCurrentUserName] = useState(null);
  const [currentUserImage, setCurrentUserImage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [usersForMentions, setUsersForMentions] = useState([]);
  const [usersMentionLoading, setUsersMentionLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch and set current user profile data
  useEffect(() => {
    if (!email) {
      setError("Email ID is missing in localStorage.");
      setPageLoading(false);
      return;
    }
    api.get(`users/profile/`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } })
      .then(res => {
        setCurrentUserName(res.data.username);
        setCurrentUserImage(res.data.image_url);
      })
      .catch(err => {
        console.error("Failed to fetch user profile:", err);
        setError("Failed to fetch user information.");
      });
  }, [email]);

  // Load comments list for segment
  const fetchComments = useCallback(async () => {
    if (!segmentId) return;
    setPageLoading(true);
    setError(null);
    try {
      const res = await api.get(`discussion/list/?segment=${segmentId}`, {
        headers: { Authorization: `Token ${localStorage.getItem("Token")}` }
      });
      // Normalize each comment and resolve usernames
      const normalized = await Promise.all(res.data.map(async raw => {
        let name = String(raw.user);
        try {
          const userRes = await api.get(`users/get_username_by_id/`, {
            params: { user_id: raw.user },
            headers: { Authorization: `Token ${localStorage.getItem("Token")}` }
          });
          if (userRes.data.username) name = userRes.data.username;
        } catch {
          // Silently ignore failed username lookup
        }
        const item = { ...raw, user: name };
        return normalizeCommentData(item);
      }));
      setComments(normalized);
    } catch (err) {
      console.error("Failed to fetch comments:", err);
      setError("Failed to fetch comments. Please try again.");
    } finally {
      setPageLoading(false);
    }
  }, [segmentId]);

  // Load unread badge count
  const fetchUnreadCount = useCallback(async () => {
    if (!segmentId) return;
    try {
      const res = await api.get(`discussion/unread_count/?segment_id=${segmentId}`, {
        headers: { Authorization: `Token ${localStorage.getItem("Token")}` }
      });
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      console.error("Error fetching unread count:", err);
      setUnreadCount(0);
    }
  }, [segmentId]);

  // Initial load on segment change
  useEffect(() => {
    if (segmentId) {
      fetchComments();
      setSelectedMentions([]);
      fetchUnreadCount();
    } else {
      setError("Segment ID is missing.");
      setPageLoading(false);
    }
  }, [segmentId, fetchComments, fetchUnreadCount]);

  // Auto-scroll on new comments
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  // Handle comment submission
  const handleCommentSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!currentUserName || !commentInput.trim()) return;
    setSubmitLoading(true);
    setError(null);
    try {
      const res = await api.post(`discussion/create/`, {
        segment: segmentId,
        userId: email,
        comment: commentInput,
        mentions: selectedMentions,
      }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });

      const entity = res.data.comment;
      if (!entity) throw new Error("Unexpected response structure");

      // Build UI-friendly object
      const newComment = normalizeCommentData({
        id: entity.id,
        segment: entity.segment,
        user: currentUserName,
        comment: entity.comment,
        timestamp: entity.timestamp,
        is_read: true,
        is_pinned: entity.is_pinned,
      });
      setComments(prev => [...prev, newComment]);
      setCommentInput("");
      setSelectedMentions([]);
      fetchUnreadCount();
    } catch (err) {
      console.error("Failed to post comment:", err);
      setError(err.response?.data?.detail || "Failed to post comment. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  }, [commentInput, currentUserName, segmentId, email, selectedMentions, fetchUnreadCount]);

  // Load mentionable users
  const fetchUsersForMentions = useCallback(async () => {
    if (!segmentId) return;
    setUsersMentionLoading(true);
    try {
      const res = await api.get(`api/segments/get_users_by_segment/?segment_id=${segmentId}`, {
        headers: { Authorization: `Token ${localStorage.getItem("Token")}` }
      });
      setUsersForMentions(Array.isArray(res.data) ? res.data : res.data.users || []);
    } catch (err) {
      console.error("Error fetching users for mentions:", err);
      setUsersForMentions([]);
    } finally {
      setUsersMentionLoading(false);
    }
  }, [segmentId]);

  // Mark passed comment as read
  const markCommentAsRead = useCallback(async (commentId) => {
    const index = comments.findIndex(c => c.id === commentId);
    if (index < 0 || comments[index].is_read) return;
    // Optimistically update UI
    const updated = comments.map(c => c.id === commentId ? {...c, is_read: true} : c);
    setComments(updated);
    setUnreadCount(prev => Math.max(prev - 1, 0));
    try {
      await api.post(`discussion/mark_as_read/`,
        { segment_id: segmentId, discussion_id: commentId },
        { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }
      );
    } catch (err) {
      // Revert on error
      setComments(comments);
      setUnreadCount(prev => prev + 1);
      setError("Failed to mark comment as read.");
      console.error(err);
    }
  }, [comments, segmentId]);

  // Toggle pin state for a comment
  const handlePin = useCallback((commentId) => {
    const target = comments.find(c => c.id === commentId);
    if (!target) return;
    const newStatus = !target.is_pinned;
    // Unpin others if pinning this one
    const updated = comments.map(c => ({ ...c, is_pinned: c.id === commentId ? newStatus : false }));
    setComments(updated);
    // TODO: Persist pin state via API if needed
  }, [comments]);

  return (
    <Paper sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <ChatHeader
        userName={currentUserName}
        userImage={currentUserImage}
        title={segmentId ? `Discussion for Segment ${segmentId}` : "Project Discussion"}
      />
      <Box id="messages" sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: "#fff" }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Unread Comments: {unreadCount}
        </Typography>
        {pageLoading && <CircularProgress sx={{ display: "block", mx: "auto", my: 2 }} />}
        {error && !pageLoading && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>}
        {!pageLoading && comments.length === 0 && (
          <Typography color="textSecondary" align="center" sx={{ mt: 2 }}>
            No comments yet. Be the first to comment!
          </Typography>
        )}
        <List>
  {comments.map((c, index) => {
    // Get the current comment's date and the previous comment's date
    const currentCommentDate = new Date(c.timestamp).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    const previousCommentDate =
      index > 0
        ? new Date(comments[index - 1].timestamp).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : null;

    return (
      <React.Fragment key={c.id}>
        {/* Render date separator if the date changes */}
        {currentCommentDate !== previousCommentDate && (
          <Typography
            variant="subtitle2"
            align="center"
            sx={{
              my: 2,
              color: "#6b7280",
              fontWeight: "bold",
              textTransform: "uppercase",
            }}
          >
            {currentCommentDate}
          </Typography>
        )}

        <ListItem
          sx={{
            justifyContent: c.user === currentUserName ? "flex-end" : "flex-start",
            mb: 1,
            py: 0,
          }}
          button={c.user !== currentUserName && !c.is_read}
          onClick={() =>
            c.user !== currentUserName && !c.is_read && markCommentAsRead(c.id)
          }
        >
          <Paper
            elevation={c.is_pinned ? 4 : 2}
            sx={{
              p: 1.5,
              bgcolor:
                c.user === currentUserName
                  ? "#e3f2fd"
                  : c.is_read
                  ? c.is_pinned
                    ? "#fff9c4"
                    : "#f5f5f5"
                  : c.is_pinned
                  ? "#ffecb3"
                  : "#ffebee",
              borderRadius: 2,
              maxWidth: "70%",
              position: "relative",
            }}
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography fontWeight="bold">{c.user}</Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePin(c.id);
                    }}
                  >
                    <StarBorderIcon
                      fontSize="small"
                      color={c.is_pinned ? "primary" : "action"}
                    />
                  </IconButton>
                </Box>
              }
              secondary={
                <>
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: c.comment }} />
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      textAlign: c.user === currentUserName ? "right" : "left",
                      mt: 0.5,
                    }}
                  >
                    {new Date(c.timestamp).toLocaleString("en-IN", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </>
              }
            />
            {/* Read/unread icon badge */}
            {c.user !== currentUserName && (
              <Box sx={{ position: "absolute", top: 8, right: 40 }}>
                {c.is_read ? (
                  <Badge
                    color="success"
                    badgeContent={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                  />
                ) : (
                  <Badge
                    color="error"
                    badgeContent={<VisibilityIcon sx={{ fontSize: 16 }} />}
                  />
                )}
              </Box>
            )}
          </Paper>
        </ListItem>
      </React.Fragment>
    );
  })}
</List>
        <div ref={messagesEndRef} />
      </Box>
      <ChatInput
        comment={commentInput}
        setComment={setCommentInput}
        handleCommentSubmit={handleCommentSubmit}
        loading={submitLoading}
        fetchUsersbySegment={fetchUsersForMentions}
        users={usersForMentions}
        usersLoading={usersMentionLoading}
        setSelectedMentions={setSelectedMentions}
      />
    </Paper>
  );
};

export default JTDiscussionPage;
